import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ExternalLink, Loader2, Megaphone } from "lucide-react";
import type { PartnerAdModerationItem, PartnerAdCampaignReview } from "@workspace/api-client-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
type StatusFilter = "pending" | "approved" | "rejected" | "all";

const STATUS_FILTERS: StatusFilter[] = ["pending", "approved", "rejected", "all"];

async function getCampaigns(): Promise<PartnerAdModerationItem[]> {
  const response = await fetch(`${BASE}/api/admin/partner-ad-campaigns`, { credentials: "include" });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || "Campaigns could not be loaded.");
  }
  return response.json();
}

function industryName(type: string): string {
  return type.replaceAll("_", " ").replace(/\b\w/g, letter => letter.toUpperCase());
}

export function AdminPartnerAds() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("pending");
  const [reviewNotes, setReviewNotes] = useState<Record<number, string>>({});
  const query = useQuery({
    queryKey: ["admin-partner-ad-campaigns"],
    queryFn: getCampaigns,
  });
  const review = useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & PartnerAdCampaignReview) => {
      const response = await fetch(`${BASE}/api/admin/partner-ad-campaigns/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || "The review action failed.");
      }
      return response.json() as Promise<PartnerAdModerationItem>;
    },
    onSuccess: async campaign => {
      setReviewNotes(current => ({ ...current, [campaign.id]: "" }));
      await queryClient.invalidateQueries({ queryKey: ["admin-partner-ad-campaigns"] });
    },
  });

  const campaigns = useMemo(() => (query.data ?? []).filter(campaign =>
    filter === "all" || campaign.status === filter,
  ), [query.data, filter]);

  if (query.isLoading) {
    return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" />Loading campaigns…</div>;
  }
  if (query.isError) {
    return (
      <div className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4" role="alert">
        <p>{query.error instanceof Error ? query.error.message : "Campaigns could not be loaded."}</p>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>Try again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Partner ad review</h2>
          <p className="text-sm text-muted-foreground">Only approve content that meets pilot terms. No payment is collected in the app.</p>
        </div>
        <div className="flex flex-wrap gap-2" aria-label="Filter campaigns">
          {STATUS_FILTERS.map(status => (
            <Button key={status} size="sm" variant={filter === status ? "default" : "outline"} onClick={() => setFilter(status)}>
              {status === "all" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>
      {campaigns.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          {filter === "pending" ? "No campaigns are waiting for review." : `No ${filter === "all" ? "" : `${filter} `}campaigns found.`}
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {campaigns.map(campaign => (
            <Card key={campaign.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base"><Megaphone className="h-4 w-4 text-primary" />{campaign.title}</CardTitle>
                    <CardDescription className="mt-1">{campaign.advertiserName} · {industryName(campaign.advertiserCategory)}</CardDescription>
                  </div>
                  <Badge variant={campaign.status === "approved" ? "default" : campaign.status === "rejected" ? "destructive" : "secondary"}>
                    {campaign.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {campaign.imageUrls.length > 0 && (
                  <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label={`Submitted flyer images for ${campaign.title}`}>
                    {campaign.imageUrls.map((imageUrl, index) => (
                      <img
                        key={imageUrl}
                        src={imageUrl}
                        alt={`Flyer image ${index + 1} for ${campaign.title}`}
                        className="aspect-[16/7] w-full rounded-md border bg-muted object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    ))}
                  </div>
                )}
                <p className="text-sm">{campaign.description}</p>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>Audience: {campaign.targetIndustryTypes.length ? campaign.targetIndustryTypes.map(industryName).join(", ") : "All opted-in stores"}</p>
                  <p>Schedule: {format(new Date(campaign.startsAt), "MMM d, yyyy h:mm a")} – {format(new Date(campaign.endsAt), "MMM d, yyyy h:mm a")}</p>
                  <p>Engagement: {campaign.viewCount} views · {campaign.clickCount} clicks</p>
                </div>
                <a className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline" href={campaign.destinationUrl} target="_blank" rel="noopener noreferrer">
                  Open destination <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                {campaign.reviewNote && <p className="rounded-md bg-muted p-3 text-sm">Last review note: {campaign.reviewNote}</p>}
                {campaign.status !== "approved" && (
                  <Button
                    size="sm"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: campaign.id, status: "approved", reviewNote: reviewNotes[campaign.id] || undefined })}
                  >
                    {review.isPending && review.variables?.id === campaign.id && review.variables.status === "approved" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Approve & publish
                  </Button>
                )}
                <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                  <Input
                    value={reviewNotes[campaign.id] ?? ""}
                    onChange={event => setReviewNotes(current => ({ ...current, [campaign.id]: event.target.value.slice(0, 240) }))}
                    maxLength={240}
                    placeholder="Optional review note"
                    aria-label={`Review note for ${campaign.title}`}
                    className="min-w-48 flex-1"
                  />
                  {campaign.status !== "rejected" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={review.isPending}
                      onClick={() => review.mutate({ id: campaign.id, status: "rejected", reviewNote: reviewNotes[campaign.id] || undefined })}
                    >
                      {review.isPending && review.variables?.id === campaign.id && review.variables.status === "rejected" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Reject / stop
                    </Button>
                  )}
                </div>
                {review.isError && review.variables?.id === campaign.id && <p className="text-sm text-destructive" role="alert">{review.error.message}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
